import {
  createChatConversation,
  fetchChatDirectory,
} from "@/services/api/chat";
import type { ChatUser } from "../data/types";

export function normalizePhoneDigits(value: string) {
  return value.replace(/\D/g, "");
}

function matchPeerByPhone(users: ChatUser[], phone: string): ChatUser | undefined {
  const digits = normalizePhoneDigits(phone);
  if (!digits) return undefined;
  const trimmed = phone.trim();
  return users.find((user) => {
    const ext = normalizePhoneDigits(user.extension || user.username || "");
    return ext === digits || (user.username || "") === trimmed;
  });
}

function pickBestNameMatch(users: ChatUser[], name: string): ChatUser | undefined {
  const needle = name.trim().toLowerCase();
  if (!needle) return undefined;
  const exact = users.find(
    (user) => (user.name || "").trim().toLowerCase() === needle,
  );
  if (exact) return exact;
  if (users.length === 1) return users[0];
  return undefined;
}

/**
 * Get-or-create the notes-to-self DM (peer_user_id === current user).
 * Never falls back to phone/name matching — that would open someone else's chat.
 */
export async function openOrCreateSelfDm(userId: number): Promise<string> {
  const conversation = await createChatConversation({
    type: "dm",
    peer_user_id: userId,
  });
  if (!conversation?.id) {
    throw new Error("Self DM was created but no conversation id was returned.");
  }
  return String(conversation.id);
}

/**
 * Resolve an org user and get-or-create a DM conversation.
 * Matches dialer behavior: phone/extension first, then email, then name.
 * Prefer `peerUserId` when you already know the teammate (or use openOrCreateSelfDm for notes).
 */
export async function openOrCreateDmForContact(input: {
  phone?: string | null;
  email?: string | null;
  name?: string | null;
  peerUserId?: number | null;
}): Promise<string | null> {
  const phone = (input.phone || "").trim();
  const email = (input.email || "").trim();
  const name = (input.name || "").trim();

  try {
    if (input.peerUserId != null && Number.isFinite(Number(input.peerUserId))) {
      const conversation = await createChatConversation({
        type: "dm",
        peer_user_id: Number(input.peerUserId),
      });
      return conversation?.id ? String(conversation.id) : null;
    }

    if (phone) {
      const digits = normalizePhoneDigits(phone);
      if (digits) {
        const users = await fetchChatDirectory(digits);
        const peer = matchPeerByPhone(users, phone);
        if (peer) {
          const conversation = await createChatConversation({
            type: "dm",
            peer_user_id: peer.id,
          });
          return conversation?.id ? String(conversation.id) : null;
        }
      }
    }

    if (email) {
      const users = await fetchChatDirectory(email);
      const peer = users.length === 1 ? users[0] : undefined;
      if (peer) {
        const conversation = await createChatConversation({
          type: "dm",
          peer_user_id: peer.id,
        });
        return conversation?.id ? String(conversation.id) : null;
      }
    }

    if (name) {
      const users = await fetchChatDirectory(name);
      const peer = pickBestNameMatch(users, name);
      if (peer) {
        const conversation = await createChatConversation({
          type: "dm",
          peer_user_id: peer.id,
        });
        return conversation?.id ? String(conversation.id) : null;
      }
    }

    return null;
  } catch {
    return null;
  }
}
